import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../services/supabase';
import FluidBackground from '../FluidBackground';
import Header from '../Header';
import Footer from '../Footer';
import Login from '../Login';
import AuthDialog from '../AuthDialog';
import ListingLandingPage from './ListingLandingPage';
import ListingProcessing from './ListingProcessing';
import ListingPreviewPage from './ListingPreviewPage';
import { FUNNEL_BASE_PATH } from './constants';
import { claimListingPreviews, logListingEvent } from '../../services/listing-funnel';

type FunnelView = 'landing' | 'processing' | 'preview';

interface FunnelRouteInfo {
  view: FunnelView;
  param?: string;
}

function parseFunnelRoute(): FunnelRouteInfo {
  const path = window.location.pathname;
  if (path === FUNNEL_BASE_PATH || path === FUNNEL_BASE_PATH + '/') {
    return { view: 'landing' };
  }
  const procMatch = path.match(new RegExp(`^${FUNNEL_BASE_PATH}/processing/([a-zA-Z0-9-]+)$`));
  if (procMatch) return { view: 'processing', param: procMatch[1] };
  const prevMatch = path.match(new RegExp(`^${FUNNEL_BASE_PATH}/preview/([a-zA-Z0-9]+)$`));
  if (prevMatch) return { view: 'preview', param: prevMatch[1] };
  return { view: 'landing' };
}

export default function ListingFunnelRouter() {
  const [routeInfo, setRouteInfo] = useState<FunnelRouteInfo>(parseFunnelRoute);
  const [user, setUser] = useState<User | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [processingJobId, setProcessingJobId] = useState<string | null>(null);
  const [processingShareSlug, setProcessingShareSlug] = useState<string | null>(null);
  const [processingImages, setProcessingImages] = useState<string[]>([]);
  const handoffDoneRef = useRef(false);

  // Lightweight auth check (no redirect to dashboard)
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      // Check for SSO tokens
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const ssoAccessToken = hashParams.get('sso_access_token');
      const ssoRefreshToken = hashParams.get('sso_refresh_token');

      if (ssoAccessToken && ssoRefreshToken) {
        window.history.replaceState({}, document.title, window.location.pathname);
        try {
          const { data } = await supabase.auth.setSession({
            access_token: ssoAccessToken,
            refresh_token: ssoRefreshToken,
          });
          if (data.session && !cancelled) {
            setUser(data.session.user);
          }
        } catch { /* SSO failed, continue as anon */ }
        return;
      }

      try {
        const { data: { user: existingUser } } = await supabase.auth.getUser();
        if (!cancelled && existingUser) {
          setUser(existingUser);
        }
      } catch { /* no session */ }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (cancelled) return;
        if (event === 'SIGNED_IN' && newSession) {
          setUser(newSession.user);
          setShowLoginModal(false);

          // Account handoff: claim anonymous previews
          if (!handoffDoneRef.current) {
            handoffDoneRef.current = true;
            const claimed = await claimListingPreviews();
            if (claimed > 0) {
              logListingEvent('signup_completed', { claimed });
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          handoffDoneRef.current = false;
        } else if (event === 'TOKEN_REFRESHED' && newSession) {
          setUser(newSession.user);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Navigation helpers
  const navigateTo = useCallback((view: FunnelView, param?: string) => {
    let newPath = FUNNEL_BASE_PATH;
    if (view === 'processing' && param) newPath = `${FUNNEL_BASE_PATH}/processing/${param}`;
    if (view === 'preview' && param) newPath = `${FUNNEL_BASE_PATH}/preview/${param}`;
    window.history.pushState({}, '', newPath);
    setRouteInfo({ view, param });
  }, []);

  const handleStartProcessing = useCallback((jobId: string, shareSlug: string, images: string[]) => {
    setProcessingJobId(jobId);
    setProcessingShareSlug(shareSlug);
    setProcessingImages(images);
    navigateTo('processing', jobId);
  }, [navigateTo]);

  const handleShowPreview = useCallback((shareSlug: string) => {
    navigateTo('preview', shareSlug);
  }, [navigateTo]);

  const handleProcessingComplete = useCallback((shareSlug: string) => {
    navigateTo('preview', shareSlug);
  }, [navigateTo]);

  const handleProcessingError = useCallback(() => {
    // Stay on processing page — it shows its own error state
  }, []);

  const openSignup = useCallback(() => {
    setAuthMode('signup');
    setShowLoginModal(true);
  }, []);

  const openLogin = useCallback(() => {
    setAuthMode('login');
    setShowLoginModal(true);
  }, []);

  // Popstate handler for back/forward
  useEffect(() => {
    const handlePopState = () => {
      setRouteInfo(parseFunnelRoute());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
      <FluidBackground />
      <div id="public-entry" className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
        <Header
          appearance="entry"
          onReset={() => navigateTo('landing')}
          showNewProject={false}
          onNewProject={() => {}}
          onSignOut={async () => {
            await supabase.auth.signOut();
            setUser(null);
          }}
          onLogin={openLogin}
          onSignUp={openSignup}
          userEmail={user?.email}
        />

        {routeInfo.view === 'landing' && (
          <ListingLandingPage
            onStartProcessing={handleStartProcessing}
            onShowPreview={handleShowPreview}
          />
        )}

        {routeInfo.view === 'processing' && (processingJobId || routeInfo.param) && (
          <ListingProcessing
            jobId={processingJobId ?? routeInfo.param!}
            shareSlug={processingShareSlug ?? ''}
            initialImages={processingImages}
            onComplete={handleProcessingComplete}
            onError={handleProcessingError}
          />
        )}

        {routeInfo.view === 'preview' && routeInfo.param && (
          <ListingPreviewPage
            shareSlug={routeInfo.param}
            user={user}
            onSignUp={openSignup}
            onLogin={openLogin}
          />
        )}

        <div className="shrink-0">
          <Footer />
        </div>
      </div>

      {showLoginModal && (
        <AuthDialog onClose={() => setShowLoginModal(false)}>
          <Login
            onAuthSuccess={() => setShowLoginModal(false)}
            onClose={() => setShowLoginModal(false)}
            initialMode={authMode}
          />
        </AuthDialog>
      )}
    </div>
  );
}
